// Which zone this person's clock means — the setting that decides what "09:00"
// in the composer is an instruction to do.
//
// It is on Profile and not in workspace settings because it follows the person:
// the planner in Mumbai and the account manager running that client's US launch
// share a workspace and do not share a clock. See the 20260920000300 migration.
//
// COUNTRY FIRST. Nobody is looking for `Asia/Kolkata`. They are looking for
// India, and then — only in the handful of countries with more than one zone —
// for which part of it. Two selects in that order turn a flat list of 418
// identifiers into one question most people answer in a single click, because
// 150-odd countries have exactly one zone and the second select fills itself.

import { useEffect, useMemo, useState } from "react";
import { COUNTRY_ZONES, COUNTRY_OF_ZONE } from "../lib/zones.generated";
import {
  deviceZone, formatInZone, offsetLabel, zoneLabel, zoneName,
} from "../lib/timezone";
import { useUserSettings } from "../lib/userSettings";
import { guessCountry } from "../lib/entitlements";
import { logError } from "../lib/logger";
import { Spinner } from "./Spinner";

/**
 * Where to open the picker for somebody who has never set it.
 *
 * The device's zone is the strongest signal available and the one the app has
 * been silently using all along, so the tz database's own answer for it comes
 * first. `guessCountry` reads the locale instead and is the fallback for the
 * zones filed under no country at all — `UTC`, the `Etc/*` family.
 */
function initialCountry(saved: string | null, zone: string): string {
  return saved ?? COUNTRY_OF_ZONE.get(zone) ?? guessCountry() ?? "US";
}

export function TimeZoneCard() {
  const { timezone, country_code, zone, automatic, ready, save } = useUserSettings();

  const device = deviceZone();
  const [country, setCountry] = useState(() => initialCountry(country_code, device));
  const [picked, setPicked] = useState(() => timezone ?? device);
  const [auto, setAuto] = useState(automatic);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The first load lands after the first render, so the controls start on the
  // device's answer and move to the saved one when it arrives.
  useEffect(() => {
    if (!ready) return;
    setAuto(automatic);
    setPicked(timezone ?? device);
    setCountry(initialCountry(country_code, timezone ?? device));
  }, [ready, automatic, timezone, country_code, device]);

  const zonesHere = useMemo(
    () => COUNTRY_ZONES.find((c) => c.code === country)?.zones ?? [],
    [country],
  );

  // A zone the tz database does not file under the chosen country: the person
  // changed country and the old zone is still selected. Offering it in a list
  // that does not contain it would render a blank select, so the first zone of
  // the new country stands in until they pick.
  const effective = zonesHere.some((z) => z.id === picked) ? picked : zonesHere[0]?.id ?? device;

  const chosen = auto ? device : effective;
  const dirty = auto !== automatic || (!auto && effective !== (timezone ?? device));

  async function apply() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await save({
        timezone: auto ? null : effective,
        // Kept even when following the device, so reopening the card lands on
        // the country they last looked at rather than re-deriving it.
        country_code: auto ? null : country,
      });
      setSaved(true);
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "save_timezone" });
    } finally {
      setBusy(false);
    }
  }

  // A saved zone this browser cannot resolve. The context has already fallen
  // back to the device so nothing on screen is wrong — but the preference the
  // person set is not the one in force, and only this card can say so.
  const stale = timezone !== null && zone !== timezone;

  const sample = useMemo(() => {
    // 9am tomorrow in the chosen zone: the time somebody actually schedules,
    // rather than "now", which reads the same in every zone they compare.
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d;
  }, []);

  return (
    <div className="card">
      <h2>Your time zone</h2>
      <p className="muted" style={{ maxWidth: "62ch" }}>
        The clock the composer and the calendar speak. Pick 9:00 for a post and this is the
        9:00 it means — so you can plan a client's morning from anywhere without doing the
        arithmetic, and so a colleague in another country reads back the time you intended
        rather than the time their own laptop is on.
      </p>

      {error && <div className="card err">{error}</div>}

      {stale && (
        <div className="notice warn">
          Your saved zone <code>{timezone}</code> is not one this browser knows, so times are
          showing in {zoneName(device)} for now. Pick it again below.
        </div>
      )}

      <dl className="kv">
        <dt>In force</dt>
        <dd>
          {zoneLabel(zone)}
          {automatic && <span className="muted"> · following this device</span>}
        </dd>
        <dt>Right now</dt>
        <dd>{formatInZone(new Date(), zone, { dateStyle: "full", timeStyle: "short" })}</dd>
      </dl>

      <label className="row" style={{ marginTop: ".8rem", gap: ".5rem" }}>
        <input type="checkbox" checked={auto} disabled={busy}
               onChange={(e) => setAuto(e.target.checked)} />
        <span>
          Follow this device automatically
          <span className="muted"> — {zoneName(device)} ({`UTC${offsetLabel(device)}`}) here</span>
        </span>
      </label>

      <div className="row" style={{ marginTop: ".8rem", alignItems: "flex-end", gap: ".8rem" }}>
        <div>
          <label htmlFor="tz-country">Country</label>
          <select id="tz-country" value={country} disabled={auto || busy}
                  onChange={(e) => setCountry(e.target.value)}>
            {COUNTRY_ZONES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Hidden rather than disabled where the country has one zone: a select
            with a single option is a question with one answer, and showing it
            only invites a click that does nothing. */}
        {zonesHere.length > 1 && (
          <div>
            <label htmlFor="tz-zone">Time zone</label>
            <select id="tz-zone" value={effective} disabled={auto || busy}
                    onChange={(e) => setPicked(e.target.value)}>
              {zonesHere.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.region ? `${z.region} — ` : ""}{zoneLabel(z.id)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <p className="muted" style={{ marginTop: ".6rem" }}>
        {auto ? "Times follow whichever device you are on." : <>Times will show as {zoneLabel(chosen)}.</>}
        {" "}Schedule a post for 9:00 tomorrow and it goes out at{" "}
        <strong>{formatInZone(sample, chosen, { timeStyle: "short" })}</strong> in {zoneName(chosen)}
        {chosen !== device && (
          <> — {formatInZone(sample, device, { timeStyle: "short" })} on this device</>
        )}.
      </p>

      <div className="row" style={{ marginTop: ".8rem" }}>
        <button className="primary sm" disabled={busy || !dirty} onClick={() => void apply()}>
          {busy ? <><Spinner /> Saving…</> : "Save time zone"}
        </button>
        {saved && !dirty && <span className="muted">Saved.</span>}
      </div>
    </div>
  );
}
